import React, { useState, useEffect } from "react";
import {
    ShoppingCart, X, CreditCard, Navigation, MapPin, 
    CheckCircle, Trash2, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

export const CartModal = ({ cartItems, onClose, onRemoveFromCart, onClearCart, sellerId }) => {
    const buyerData = JSON.parse(localStorage.getItem("buyer_user") || "{}");
    const token = localStorage.getItem("buyer_token");
    
    const [loading, setLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(buyerData.phone || "");
    const [deliveryType, setDeliveryType] = useState("pickup");
    const [paymentMethod, setPaymentMethod] = useState("moncash");
    const [bvgpayBalance, setBvgpayBalance] = useState(null);
    const [address, setAddress] = useState("");
    const [coords, setCoords] = useState(null);
    const [proposedFee, setProposedFee] = useState("");
    const [deliveryVehicle, setDeliveryVehicle] = useState("moto");
    const [calculatedKm, setCalculatedKm] = useState(null);
    const [buyForSomeone, setBuyForSomeone] = useState(false);
    const [recipientName, setRecipientName] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("");
    const [onlineDrivers, setOnlineDrivers] = useState([]);
    const [showDriversList, setShowDriversList] = useState(false);

    const fetchOnlineDrivers = async () => {
        try {
            const res = await axios.get(`${API}/drivers/list-active?_t=${Date.now()}`);
            if (res.data?.success) {
                setOnlineDrivers(res.data.drivers || []);
            }
        } catch (e) {}
    };

    useEffect(() => {
        fetchOnlineDrivers();
        const interval = setInterval(fetchOnlineDrivers, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (token) {
            axios.get(`${API}/linkamazon/bvgpay/wallet`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => setBvgpayBalance(res.data.balance_htg))
                .catch(() => {});
        }
    }, [token]);

    const subtotal = cartItems.reduce((acc, item) => acc + (item.price_htg * item.qty), 0);
    const platformFeeRate = paymentMethod === 'bvgpay' ? 0.05 : 0.08;
    const platformFee = Math.ceil(subtotal * platformFeeRate);
    const deliveryFee = deliveryType === "delivery" ? (parseFloat(proposedFee) || 0) : 0;
    const finalTotal = subtotal + platformFee + deliveryFee;

    const haversineKm = (lat1, lon1, lat2, lon2) => {
        const R = 6371; 
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        return R * c;
    };

    const computeFee = (km, vehicle) => {
        const rate = vehicle === 'machine' ? 50 : 25; // 25 HTG/KM for moto, 50 HTG/KM for machine
        const minFee = vehicle === 'machine' ? 50 : 25;
        const raw = Math.ceil(km * rate);
        return Math.max(minFee, raw);
    };

    const handleVehicleChange = (newVehicle) => {
        setDeliveryVehicle(newVehicle);
        const km = calculatedKm || 2.0;
        const fee = computeFee(km, newVehicle);
        setProposedFee(fee.toString());
    };

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Navigatè w la pa sipòte GPS.");
            return;
        }

        toast.info("N'ap chache pozisyon w...");
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
                
                let sellerLoc = cartItems[0]?.seller_location;
                let distKm = 2.0; // Default 2 KM
                
                if (sellerLoc && sellerLoc.lat && sellerLoc.lng) {
                    distKm = haversineKm(sellerLoc.lat, sellerLoc.lng, position.coords.latitude, position.coords.longitude);
                    distKm = Math.max(0.5, distKm);
                    toast.success(`Distans kalkile: ${distKm.toFixed(1)} KM`);
                } else {
                    distKm = 2.0;
                    toast.info("Lokalizasyon anrejistre! (Estimasyon baz: 2 KM)");
                }

                setCalculatedKm(distKm);
                const fee = computeFee(distKm, deliveryVehicle);
                setProposedFee(fee.toString());
                toast.success("Lokalizasyon anrejistre avèk siksè! ✅");
            },
            (error) => {
                toast.error("Mwen pa ka jwenn lokalizasyon w. Tcheke si GPS la aktive.");
                // Set default 2KM so client is not blocked
                const distKm = 2.0;
                setCalculatedKm(distKm);
                const fee = computeFee(distKm, deliveryVehicle);
                setProposedFee(fee.toString());
            }
        );
    };

    const handleCheckout = async () => {
        if (cartItems.length === 0) return;
        
        if (!phoneNumber || phoneNumber.length < 8) {
            toast.error(paymentMethod === "moncash" ? "Tanpri antre yon nimewo MonCash valid." : "Tanpri antre yon nimewo telefAn valid.");
            return;
        }

        if (buyForSomeone) {
            if (!recipientName || recipientName.length < 2) {
                toast.error("Tanpri mete non moun k'ap resevwa a korekteman.");
                return;
            }
            if (!recipientPhone || recipientPhone.length < 8) {
                toast.error("Tanpri mete nimewo telefAn moun k'ap resevwa a.");
                return;
            }
        }

        if (deliveryType === "delivery") {
            if (!address) {
                toast.error("Tanpri antre adres livrezon an.");
                return;
            }
            if (!proposedFee || proposedFee <= 0) {
                toast.error("Tanpri klike sou 'Voye pozisyon m' pou kalkile pri livrezon an.");
                return;
            }
        }

        setLoading(true);
        try {
            const res = await axios.post(`${API}/marketplace/orders`, {
                buyer_name: buyerData.first_name ? `${buyerData.first_name} ${buyerData.last_name || ''}` : "Kliyan",
                buyer_phone: phoneNumber,
                delivery_address: deliveryType === 'pickup' ? "pickup" : address,
                location: coords,
                seller_id: sellerId || cartItems[0]?.seller_id,
                items: cartItems.map(item => ({
                    product_id: item.id,
                    product_name: item.title || item.name,
                    price: Number(item.price_htg),
                    quantity: item.qty
                })),
                total_htg: finalTotal,
                payment_method: paymentMethod,
                delivery_method: deliveryType,
                delivery_fee: deliveryType === 'delivery' ? parseFloat(proposedFee) : 0,
                recipient_name: buyForSomeone ? recipientName : null,
                recipient_phone: buyForSomeone ? recipientPhone : null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                const orderId = res.data.order_id;
                
                if (paymentMethod === "bvgpay") {
                    const payRes = await axios.post(`${API}/marketplace/orders/${orderId}/pay-bvgpay`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (payRes.data.success) {
                        toast.success("Acha a fet avek sikse kounye a!");
                        onClearCart();
                        setTimeout(() => {
                            onClose();
                            window.location.href = `/order/${orderId}`;
                        }, 1500);
                    }
                } else {
                    const payRes = await axios.post(`${API}/marketplace/orders/${orderId}/pay-moncash`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (payRes.data.redirect_url) {
                        onClearCart();
                        window.location.href = payRes.data.redirect_url;
                    } else {
                        toast.error("Ere nan kAmanse peman an.");
                    }
                }
            } else {
                toast.error(res.data.message || "Ere nan komande a.");
            }
        } catch (error) {
            console.error("Checkout Error:", error);
            const errMsg = error.response?.data?.message || error.response?.data?.detail || "Ere pandan peman an. Eseye ankA.";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden relative flex flex-col p-10 text-center animate-in zoom-in-95 duration-500">
                    <Button variant="ghost" onClick={onClose} className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-black/5 hover:bg-black/10 text-gray-500"><X className="w-6 h-6" /></Button>
                    <ShoppingCart className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                    <h3 className="text-2xl font-black uppercase tracking-tighter italic mb-2 text-[#232F3E]">Panye w la Vid</h3>
                    <p className="text-gray-500 mb-8 font-bold">Tanpri ajoute k?k pwodwi pou kontinye.</p>
                    <Button onClick={onClose} className="w-full h-14 bg-[#FF9900] hover:bg-[#E88B00] text-black rounded-2xl font-black uppercase tracking-tighter italic">Kontinye Achte</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-[40px] w-full max-w-xl shadow-2xl overflow-hidden relative flex flex-col max-h-[85dvh] md:max-h-[90dvh] animate-in zoom-in-95 duration-500">
                <div className="bg-[#232F3E] text-white p-4 sm:p-6 relative shrink-0">
                    <Button variant="ghost" onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full hover:bg-white/10 text-white"><X className="w-5 h-5" /></Button>
                    <h2 className="text-2xl font-black uppercase tracking-tighter italic flex items-center gap-3">
                        <ShoppingCart className="w-6 h-6 text-[#FF9900]" /> Panye Mwen
                    </h2>
                </div>

                <div className="overflow-y-auto no-scrollbar flex-1 min-h-0 p-4 sm:p-6 space-y-4 sm:space-y-6 bg-gray-50/50">
                    <div className="space-y-4">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">PWODWI YO ({cartItems.reduce((a,b)=>a+b.qty,0)})</Label>
                        {cartItems.map((item, idx) => (
                            <div key={`${item.id}-${idx}`} className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm relative group">
                                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                                    {(item.images?.[0] || item.image_url) ? (
                                        <img src={item.images?.[0] || item.image_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <ShoppingCart className="w-8 h-8 text-gray-300 m-auto mt-4" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-black uppercase tracking-tighter italic leading-tight mb-1 truncate">{item.title || item.name}</h4>
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400">{(item.price_htg).toLocaleString()} HTG A- {item.qty}</span>
                                            <div className="text-sm font-black text-[#FF9900] italic">{(item.price_htg * item.qty).toLocaleString()} HTG</div>
                                        </div>
                                        <Button variant="ghost" onClick={() => onRemoveFromCart(item.id)} className="w-8 h-8 rounded-full text-red-500 hover:bg-red-50 p-0">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">OPSYON LIVREZON</Label>
                            <button
                                type="button"
                                onClick={() => setShowDriversList(!showDriversList)}
                                className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-[11px] font-black rounded-full flex items-center gap-1.5 transition-all shadow-sm active:scale-95 cursor-pointer"
                            >
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span>{onlineDrivers.length} {onlineDrivers.length === 1 ? 'Chofè' : 'Chofè'} An Liy</span>
                            </button>
                        </div>

                        {/* EXPANDABLE ONLINE DRIVERS LIST */}
                        {showDriversList && (
                            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 p-4 rounded-2xl shadow-xl text-white animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-white/10">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                                        <h3 className="font-black text-xs uppercase tracking-wider text-emerald-400">
                                            CHOFÈ KI AN LIY KOUNYE A ({onlineDrivers.length})
                                        </h3>
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setShowDriversList(false)}
                                        className="text-slate-400 hover:text-white text-xs font-black px-2 py-0.5 rounded-lg bg-white/5 cursor-pointer"
                                    >
                                        Fèmen ✕
                                    </button>
                                </div>

                                {onlineDrivers.length === 0 ? (
                                    <p className="text-xs text-slate-400 text-center py-3 font-medium">
                                        Pa gen chofè an liy nan moman an. N ap notifye yo kou w fè kòmand lan!
                                    </p>
                                ) : (
                                    <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                                        {onlineDrivers.map(d => {
                                            const zone = d.commune || d.zone || d.department || 'Pòtoprens';
                                            const isMoto = (d.vehicle_type || '').toLowerCase().includes('moto');
                                            return (
                                                <div key={d.id || d.phone} className="flex items-center justify-between p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition-all">
                                                    <div className="flex items-center gap-3">
                                                        {d.profile_photo ? (
                                                            <img src={d.profile_photo} alt={d.fullname || d.name} className="w-10 h-10 rounded-full object-cover border border-emerald-500/40" />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-black text-sm">
                                                                {(d.fullname || d.name || 'C').slice(0, 1).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-black text-xs text-white">{d.fullname || d.name || 'Chofè'}</p>
                                                                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                                    {isMoto ? '🛵 Moto' : '🚗 Machin'}
                                                                </span>
                                                            </div>
                                                            <p className="text-[10px] font-bold text-amber-400 flex items-center gap-1 mt-0.5">
                                                                <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                                                <span>Zòn: {zone}</span>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <span className="text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full border border-emerald-500/20 flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> AN LIY
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex gap-4">
                            <Button 
                                variant="outline" 
                                onClick={() => setDeliveryType("pickup")}
                                className={`flex-1 h-14 rounded-2xl border-2 transition-all font-black uppercase tracking-tighter italic ${deliveryType === 'pickup' ? 'border-[#FF9900] bg-orange-50 text-[#FF9900]' : 'border-gray-100 bg-white text-gray-400'}`}
                            >
                                <ShoppingCart className="w-5 h-5 mr-2" /> Pickup
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => setDeliveryType("delivery")}
                                className={`flex-1 h-14 rounded-2xl border-2 transition-all font-black uppercase tracking-tighter italic ${deliveryType === 'delivery' ? 'border-[#FF9900] bg-orange-50 text-[#FF9900]' : 'border-gray-100 bg-white text-gray-400'}`}
                            >
                                <MapPin className="w-5 h-5 mr-2" /> Livrezon
                            </Button>
                        </div>
                    </div>

                    {deliveryType === "delivery" && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300 bg-orange-50/50 p-5 rounded-[24px] border border-orange-100">
                            {/* Vehicle Type Selector (Moto 25 HTG/KM vs Machin 50 HTG/KM) */}
                            <div className="space-y-1.5">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500">CHWAZI TIP VEYIKIL LIVREZON</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleVehicleChange("moto")}
                                        className={`p-2.5 rounded-xl border-2 font-black text-xs uppercase flex items-center justify-center gap-2 transition-all ${
                                            deliveryVehicle === "moto"
                                            ? "border-[#FF9900] bg-orange-100/80 text-[#FF9900] shadow-sm"
                                            : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"
                                        }`}
                                    >
                                        <span>🏍️ Moto</span>
                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FF9900]/20 text-[#FF9900] font-black">25 G/KM</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleVehicleChange("machine")}
                                        className={`p-2.5 rounded-xl border-2 font-black text-xs uppercase flex items-center justify-center gap-2 transition-all ${
                                            deliveryVehicle === "machine"
                                            ? "border-cyan-500 bg-cyan-50 text-cyan-900 shadow-sm"
                                            : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"
                                        }`}
                                    >
                                        <span>🚗 Machin</span>
                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-700 font-black">50 G/KM</span>
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-[#FF9900]">ADRÈS LIVREZON</Label>
                                <Input
                                    placeholder="Kote n'ap pote l la... (Egz: #14 Delma 33)"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="h-12 rounded-xl border-2 border-white bg-white font-bold shadow-sm text-sm"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleGetLocation}
                                    className={`w-full h-11 rounded-xl border-dashed border-2 transition-all font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 ${coords ? 'border-green-300 bg-green-50 text-green-700' : 'border-[#FF9900]/30 text-[#FF9900] hover:bg-white'}`}
                                >
                                    {coords ? <CheckCircle className="w-4 h-4" /> : <Navigation className="w-4 h-4" />}
                                    {coords ? "Lokalizasyon anrejistre (GPS) ✅" : "Voye pozisyon m pou mezire distans la (GPS)"}
                                </Button>
                            </div>

                            {proposedFee && (
                                <div className="pt-2 border-t border-orange-100/50 space-y-1">
                                    <div className="flex justify-between items-center">
                                        <Label className="text-[10px] font-black uppercase tracking-widest text-[#FF9900]">PRI LIVREZON</Label>
                                        <span className="font-black text-sm italic text-[#232F3E]">+{parseFloat(proposedFee).toLocaleString()} HTG</span>
                                    </div>
                                    <p className="text-[9px] font-bold text-gray-500 flex items-center justify-between">
                                        <span>📍 Distans: {calculatedKm ? `${calculatedKm.toFixed(1)} KM` : 'Estimasyon'}</span>
                                        <span className="font-black text-amber-700">
                                            {deliveryVehicle === 'machine' ? '50 GDS / KM (Machin)' : '25 GDS / KM (Moto)'}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="bg-gradient-to-br from-[#232F3E] to-black p-4 sm:p-6 pb-6 text-white shadow-2xl relative overflow-hidden shrink-0">
                    <div className="flex justify-between items-end mb-4">
                        <div>
                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">SOUS-TOTAL</div>
                            <div className="font-bold text-gray-300">{subtotal.toLocaleString()} HTG</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                                TOTAL (Avek Fra Sistem {paymentMethod === 'bvgpay' ? '5%' : '8%'} {deliveryType === 'delivery' && '& TranspO`'})
                            </div>
                            <div className="text-2xl font-black text-[#FF9900] italic">
                                {finalTotal.toLocaleString()} HTG
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">CHWAZI MWAYEN PEMAN OU</Label>
                        <div className="flex gap-3">
                            <Button 
                                variant="outline" 
                                onClick={() => setPaymentMethod("moncash")}
                                className={`flex-1 h-12 rounded-xl border-2 transition-all font-black uppercase tracking-widest text-xs ${paymentMethod === 'moncash' ? 'border-[#FF9900] bg-[#FF9900]/10 text-[#FF9900]' : 'border-white/10 bg-white/5 text-gray-400'}`}
                            >
                                MonCash
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => setPaymentMethod("bvgpay")}
                                className={`flex-1 h-12 relative rounded-xl border-2 transition-all font-black uppercase tracking-widest text-xs ${paymentMethod === 'bvgpay' ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400' : 'border-white/10 bg-white/5 text-gray-400'}`}
                            >
                                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-pulse">-3% FEE</div>
                                BVGPAY
                            </Button>
                        </div>
                        
                        <div className="flex shadow-2xl rounded-xl overflow-hidden focus-within:ring-2 ring-[#FF9900]/50 transition-all border border-white/10">
                            <div className="bg-white/10 px-4 flex items-center justify-center border-r border-white/5 font-black text-[#FF9900] backdrop-blur-md text-sm">+509</div>
                            <Input
                                type="tel"
                                placeholder={paymentMethod === "moncash" ? "MonCash (8 chif)" : "TelefAn Ou"}
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                className="bg-white/5 border-none h-12 text-lg font-black placeholder:text-gray-500 focus-visible:ring-0 rounded-none backdrop-blur-md text-white px-3"
                            />
                        </div>

                        <Button 
                            onClick={handleCheckout}
                            disabled={loading || cartItems.length === 0}
                            className="w-full h-14 bg-gradient-to-r from-[#FF9900] to-[#E88B00] hover:from-[#E88B00] hover:to-[#D17D00] text-black rounded-xl font-black text-lg uppercase tracking-tighter italic shadow-xl transition-all disabled:opacity-50"
                        >
                            {loading ? <span className="flex items-center gap-2"><CreditCard className="w-5 h-5 animate-spin" /> AP TRAVAY...</span> : <span className="flex items-center gap-2"><CreditCard className="w-5 h-5" /> PEYE KOUNYE A</span>}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};


